import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { ChevronDown } from 'lucide-react'

import { formatServiceStatus } from '@/help/serviceStatus'
import { NoteMarkdown } from '@/components/NoteMarkdown'
import { StatusLabelStacked } from '@/components/StatusLabelStacked'
import {
  differingOccurredLabel,
  formatRelativeDateTime,
} from '@/lib/relativeDateTime'
import { frameThrottle } from '@/lib/frameThrottle'
import { getServiceStatusMeta } from '@/lib/serviceStatusMeta'
import type { ServiceStatus, StatusChangeLog } from '@/lib/schemas/db'

export interface StatusHistoryPillProps {
  currentStatus: ServiceStatus
  statusChanges?: StatusChangeLog[]
  getAuthorDisplayName?: (authorId: string) => string
  className?: string
  dropdownStrategy?: 'fixed' | 'absolute'
  /**
   * 'pill' is the bordered button with timestamp; 'ledger' is the compact
   * dot + coloured label used inside ledger table rows.
   */
  variant?: 'pill' | 'ledger'
}

function statusDotClass(status: ServiceStatus): string {
  return getServiceStatusMeta(status).dotClass
}

export function StatusHistoryPill(props: StatusHistoryPillProps) {
  const {
    currentStatus,
    statusChanges = [],
    getAuthorDisplayName,
    className,
    dropdownStrategy = 'fixed',
    variant = 'pill',
  } = props
  const [open, setOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement | null>(null)
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const instanceIdRef = useRef(`status-history-${Math.random().toString(36).slice(2)}`)
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number; width: number } | null>(null)

  const sorted = useMemo(
    () => [...statusChanges].sort((a, b) => a.createdAt.localeCompare(b.createdAt)),
    [statusChanges],
  )
  const latestChange = sorted[sorted.length - 1]

  useLayoutEffect(() => {
    if (!open || dropdownStrategy === 'absolute') {
      setDropdownPosition(null)
      return
    }

    const trigger = triggerRef.current
    if (!trigger) return

    const viewportPadding = 8

    // One layout read + render per frame while the window resizes/scrolls,
    // not one per event.
    const recalc = frameThrottle(() => {
      const triggerRect = trigger.getBoundingClientRect()
      const maxWidth = Math.min(window.innerWidth * 0.9, 22 * 16)
      const left = Math.max(
        viewportPadding,
        Math.min(triggerRect.left, window.innerWidth - maxWidth - viewportPadding),
      )
      const top = triggerRect.bottom + 6

      setDropdownPosition({ top, left, width: maxWidth })
    })

    recalc()
    window.addEventListener('resize', recalc)
    window.addEventListener('scroll', recalc, true)
    return () => {
      window.removeEventListener('resize', recalc)
      window.removeEventListener('scroll', recalc, true)
      recalc.cancel()
    }
  }, [open, dropdownStrategy])

  useEffect(() => {
    const onOtherOpened = (event: Event) => {
      const custom = event as CustomEvent<{ id: string }>
      if (custom.detail?.id !== instanceIdRef.current) {
        setOpen(false)
      }
    }

    window.addEventListener('status-history-opened', onOtherOpened as EventListener)
    return () =>
      window.removeEventListener('status-history-opened', onOtherOpened as EventListener)
  }, [])

  useEffect(() => {
    if (!open) return

    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node
      const inDropdown = dropdownRef.current?.contains(target)
      const inTrigger = triggerRef.current?.contains(target)
      if (inDropdown || inTrigger) return
      setOpen(false)
    }

    document.addEventListener('pointerdown', onPointerDown)
    return () => document.removeEventListener('pointerdown', onPointerDown)
  }, [open])

  return (
    <div className={`relative inline-flex ${className ?? ''}`.trim()}>
      <button
        ref={triggerRef}
        type="button"
        onClick={(event) => {
          event.stopPropagation()
          setOpen((v) => {
            const next = !v
            if (next) {
              window.dispatchEvent(
                new CustomEvent('status-history-opened', {
                  detail: { id: instanceIdRef.current },
                }),
              )
            }
            return next
          })
        }}
        className={
          variant === 'ledger'
            ? 'group inline-flex cursor-pointer items-center gap-1.5 rounded px-0.5 text-[13.5px]'
            : 'inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-1 text-xs text-slate-700 hover:bg-slate-50'
        }
        aria-expanded={open}
        aria-label="Toggle status history"
      >
        <span
          className={`${variant === 'ledger' ? 'h-[7px] w-[7px]' : 'h-2 w-2'} shrink-0 rounded-full ${statusDotClass(currentStatus)}`}
        />
        {variant === 'ledger' ? (
          <span
            className={`font-medium ${getServiceStatusMeta(currentStatus).textClass}`}
          >
            <StatusLabelStacked status={currentStatus} />
          </span>
        ) : (
          <span className="flex flex-col items-start leading-tight">
            <span className="font-bold">
              <StatusLabelStacked status={currentStatus} />
            </span>
            <span className="text-[10px] text-slate-500">
              {latestChange
                ? formatRelativeDateTime(
                    latestChange.occurredAt ?? latestChange.createdAt,
                  )
                : 'No history yet'}
            </span>
          </span>
        )}
        <ChevronDown
          className={`${variant === 'ledger' ? 'h-3 w-3 text-slate-400 opacity-0 group-hover:opacity-100' : 'h-3.5 w-3.5'} transition-transform ${open ? 'rotate-180 opacity-100' : ''}`}
        />
      </button>

      {open ? (
        <div
          ref={dropdownRef}
          className={
            dropdownStrategy === 'absolute'
              ? 'absolute left-0 top-9 z-20 w-[22rem] max-w-[90vw] rounded-md border border-slate-200 bg-white p-3 shadow-lg'
              : 'fixed z-50 rounded-md border border-slate-200 bg-white p-3 shadow-lg'
          }
          style={
            dropdownStrategy === 'fixed' && dropdownPosition
              ? {
                  top: `${dropdownPosition.top}px`,
                  left: `${dropdownPosition.left}px`,
                  width: `${dropdownPosition.width}px`,
                }
              : undefined
          }
          onClick={(event) => event.stopPropagation()}
        >
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
            Status history
          </p>
          {sorted.length === 0 ? (
            <p className="text-xs text-slate-500">No status changes recorded yet.</p>
          ) : (
            <div className="space-y-2">
              {sorted.map((change) => {
                // Back-dated: lead with when it occurred, and keep when it was
                // logged visible so the audit trail stays honest — but only
                // when the two actually read differently.
                const occurred = differingOccurredLabel(
                  change.occurredAt,
                  change.createdAt,
                )
                const recordedLabel = formatRelativeDateTime(change.createdAt)
                const occurredLabel = occurred ?? recordedLabel
                const showRecorded = occurred !== null

                return (
                  <div
                    key={change.id}
                    className="rounded border border-slate-100 bg-slate-50 px-2.5 py-2"
                  >
                    <p className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-800">
                      <span className={`h-2 w-2 rounded-full ${statusDotClass(change.newStatus)}`} />
                      {formatServiceStatus(change.newStatus)}
                    </p>
                    <p className="text-[11px] text-slate-500">
                      {getAuthorDisplayName
                        ? getAuthorDisplayName(change.authorId)
                        : 'Unknown user'}{' '}
                      · {occurredLabel}
                    </p>
                    {showRecorded ? (
                      <p className="text-[11px] text-slate-400">
                        Recorded {recordedLabel}
                      </p>
                    ) : null}
                    {change.reason ? (
                      <div className="mt-1 text-[11px] text-slate-600">
                        Reason: <NoteMarkdown text={change.reason} />
                      </div>
                    ) : null}
                  </div>
                )
              })}
            </div>
          )}
        </div>
      ) : null}
    </div>
  )
}
